/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import _ from "lodash";
import { toHeaderCase } from "js-convert-case";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import config from "../config/boxDetail.config.json";

import { useBoxId } from "../provider";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_BOX_DETAIL } from "../graphql";
import { VApplication } from "../../../application";
import { AppNavigatorService } from "../../appNavigatorService";
import { capitalCase } from "change-case";
import moment from "moment-timezone";
import { StorageModule } from "@vlinder-web/storage-module-react";

const log: any = LoggerModule?.getInstance();
const storageService = StorageModule.getInstance();

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IBoxDetailMethods extends IPluginMethodMap {
  getBoxDetail(args: any): Promise<any>;
}

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

export class BoxDetailService implements IAgentPlugin {
  readonly methods: IBoxDetailMethods;

  constructor() {
    this.methods = {
      getBoxDetail: this.getBoxDetail.bind(this),
    };
  }

  async getBoxDetail(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { boxId } = useBoxId();
    try {
      if (client) {
        const res = await client.query({
          query: GET_BOX_DETAIL,
          variables: {
            filter: {
              ...args?.criteria,
              _id: boxId,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getBox) {
          log.success(`get box data in boxDetail screen :`, res?.data);
          return {
            isEmpty: res?.data?.getBox ? false : false,
            title: this.formatTitle(res?.data?.getBox),
            subtitle: this.formatSubTitle(res?.data?.getBox),
            listItems: this.formatAttributeItems(res?.data?.getBox),
            initialData: {
              boxBand:
                storageService.get("bandConfig", "local")?.band?.appCardTitle ??
                null,
              isVIP: res?.data?.getBox?.isVIP,
              deliveryOptions: this.formatDeliveryOptions(
                res?.data?.enum_shippingProviders,
                res?.data?.getBox?.shippingDetails
              ),
              isOpted: res?.data?.getBox?.shippingDetails?.isOpted,
            },
            buttons: [
              {
                variant: "primary",
                title: "Upgrade to VIP",
                key: "updateBox",
              },
              {
                variant: "primary",
                title: "Opt for mail delivery",
                key: "optMailDelivery",
              },
            ],
            boxTransactions: this.formatBoxTransactions(
              res?.data?.getBox?.boxtxes
            ),
          };
        }
      }
    } catch (err: any) {
      throw err;
    }
  }

  private formatDeliveryOptions(deliveryOptions: any, data: any) {
    let _res: any[] = [];
    let _firsthalf: any[] = [];
    deliveryOptions &&
      Array.isArray(deliveryOptions) &&
      deliveryOptions.length > 0 &&
      deliveryOptions.forEach((option: any) => {
        if (data?.isOpted && data?.provider === option) {
          _firsthalf.push({
            label: option,
            value: option,
          });
        } else {
          _res.push({
            label: option,
            value: option,
          });
        }
      });

    return [..._firsthalf, ..._res];
  }

  private formatBoxTransactions(boxTxns: any) {
    const metaData = storageService.get("bandConfig", "local")?.metaData;
    let _res: any[] = [];
    boxTxns &&
      Array.isArray(boxTxns) &&
      boxTxns.length > 0 &&
      boxTxns.forEach((txn: any) => {
        let _date =
          txn?.createdAt &&
          `${moment.tz(txn?.createdAt, metaData?.tmz).format("LLL")} ${
            metaData?.abbrevation
          }`;
        _res.push({
          title: _date,
          cardTitle: capitalCase(txn?.action),
          cardSubtitle: `By: ${txn?.user?.fullName}`,
          //cardDetailedText: txn?.user?.email,
          //url: "https://picsum.photos/200/300",
        });
      });

    return _res;
  }

  private formatTitle(box: any) {
    let formatted = box?.user?.fullName ? box?.user?.fullName : "";
    return toHeaderCase(formatted);
  }

  private formatSubTitle(box: any) {
    let formatted = box?.user?.email ? box?.user?.email : "";
    return formatted;
  }

  private formatAttributeItems(box: any) {
    const formatted: any = {
      info: config?.attributeItems,
    };
    console.log("box?.shippingDetails?.isOpted formatted", formatted);
    if (box) {
      if (box?.shippingDetails) {
        console.log("box?.shippingDetails?.isOpted", box?.shippingDetails);
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "shippingDetails"
        );
        if (index > -1) {
          formatted.info[index].subTitle =
            box?.shippingDetails?.provider ?? "Distribution Center";
        }
      }
      if (box?.masqID) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "masqID"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.masqID.toString();
        }
      }
      if (box?.costumeID) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "costumeID"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.costumeID;
        }
      }

      if (box?.isVIP.toString()) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "isVIP"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.isVIP
            ? box?.isVIP === true
              ? "Yes"
              : "No"
            : "No";
        }
      }
      if (box?.gender) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "gender"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.gender.toString();
        }
      }
      if (box?.contentStatus) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "contentStatus"
        );
        if (index > -1) {
          formatted.info[index].subTitle =
            box?.contentStatus && capitalCase(box?.contentStatus.toString());
        }
      }
      if (box?.paymentStatus) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "paymentStatus"
        );
        if (index > -1) {
          formatted.info[index].subTitle =
            box?.paymentStatus && capitalCase(box?.paymentStatus.toString());
        }
      }
      if (box?.section) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "section"
        );
        if (index > -1) {
          formatted.info[index].subTitle =
            box?.section && capitalCase(box?.section.toString());
        }
      }
      if (box?.sectionOptType) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "sectionOptType"
        );
        if (index > -1) {
          formatted.info[index].subTitle =
            box?.sectionOptType && capitalCase(box?.sectionOptType.toString());
        }
      }
      if (box?.deliveryStatus) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "deliveryStatus"
        );
        if (index > -1) {
          formatted.info[index].subTitle =
            box?.deliveryStatus && capitalCase(box?.deliveryStatus.toString());
        }
      }
    }
    return formatted?.info;
  }
}
